import {
  Backend,
  getNearbyQueryParamValue,
  getQueryParamValue,
  OrderByDirection,
} from '@freelancer/datastore/core';
import { isArray, isDefined } from '@freelancer/utils';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import { SearchActiveProjectsCollection } from './search-active-projects.types';

export function searchActiveProjectsBackend(): Backend<SearchActiveProjectsCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.DESC,
    }, // This endpoint is only used with search queries so doesn't need an ordering

    fetch: (authUid, ids, query, order) => {
      const searchCoordinates = getNearbyQueryParamValue(
        query,
        'searchCoordinates',
      );

      const projectTypes = getQueryParamValue(query, 'type');

      let params = {
        // Projections
        full_description: 'true',
        job_details: 'true',
        location_details: 'true',
        upgrade_details: 'true',

        // Filters
        countries: getQueryParamValue(query, 'country').map(
          country => country?.code,
        ),
        jobs: getQueryParamValue(query, 'skillIds')[0],
        languages: getQueryParamValue(query, 'language'),
        latitude: searchCoordinates?.latitude,
        longitude: searchCoordinates?.longitude,
        offset: query?.searchQueryParams?.offset,
        project_types: projectTypes,
        project_upgrades: getQueryParamValue(query, 'upgrades').map(upgrades =>
          Object.entries(upgrades)
            .filter(([_, isApplied]) => !!isApplied)
            .map(([upgrade, _]) => upgrade),
        )[0],
        query: query?.searchQueryParams?.query || query?.searchQueryParams?.q,
        reverse_sort:
          isArray(order) && order[0]
            ? order[0].direction === OrderByDirection.ASC || undefined
            : undefined,
        sort_field:
          isArray(order) && order[0]
            ? convertSortFieldApi(order[0].field)
            : undefined,
      };

      // When there is no project type supplied to `query`, assume the default case where
      // we're querying for all supported project types
      const isProjectTypeDefault =
        !isDefined(projectTypes) || projectTypes.length === 0;

      if (
        isProjectTypeDefault ||
        projectTypes.includes(ProjectTypeApi.HOURLY)
      ) {
        params = {
          ...params,
          ...{
            max_avg_hourly_rate: query?.searchQueryParams?.maxHourlyRate,
            min_avg_hourly_rate: query?.searchQueryParams?.minHourlyRate,
          },
        };
      }

      if (isProjectTypeDefault || projectTypes.includes(ProjectTypeApi.FIXED)) {
        params = {
          ...params,
          ...{
            max_avg_price: query?.searchQueryParams?.maxFixedPrice,
            min_avg_price: query?.searchQueryParams?.minFixedPrice,
          },
        };
      }

      return {
        endpoint: `projects/0.1/projects/active`,
        params,
      };
    },
    push: undefined,
    set: undefined,
    update: undefined,
    remove: undefined,
  };
}

// TODO: use MyProjectsContestsSearchSortFieldApi once the API thrift definitions are updated
function convertSortFieldApi(sortField: string): string | undefined {
  switch (sortField) {
    case 'bidCount':
      return 'bid_count';
    case 'maximumBudget':
      return 'bid_avg_usd';
    case 'minimumBudget':
      return 'bid_avg_usd';
    case 'timeSubmitted':
      return 'submitdate';
    default:
      return undefined;
  }
}
